import React, { useState, useMemo } from 'react';
import { DatePicker } from 'rsuite';
import { startOfDay, endOfDay, isAfter, isBefore, isSameDay } from 'date-fns';

import { Card, Flex, Text as TextTremor, Title, Button } from './TremorCompat';
import { StatusTracker } from './StatusTracker';

import { LoadingView } from './DisplayItems';

//import { paramsColors } from "../params";

import { COLOURS } from '../styles';
import { datetimeFormats } from '../constants';
import { useDeviceContext } from '../contexts/DeviceContext';
import { useConnectionContext } from '../contexts/ConnectionContext';
import { getApplianceLabel } from '../utils/applianceLabel';

export const AppliancesView = () => {
  const {
    selectedDateRange,
    applianceTrackerDataArray: AppTrackerData,
    pumpTrackerData,
    selectedDevice,
    loading,
    progress,
    isLoadingOrNoDevice: getIsLoadingOrNoDevice,
    isApplianceConfigured,
    refreshDataByDate: onSelectedDateRangeChanged,
  } = useDeviceContext();
  // isLoadingOrNoDevice is a function in useDevices' raw return (App.js used
  // to invoke it before passing the boolean down as a prop) — call it fresh
  // each render rather than treating the function reference itself as the
  // (always-truthy) loading flag.
  const isLoadingOrNoDevice = getIsLoadingOrNoDevice();

  // Appliance headings come from the controller's own name and type registers, the same
  // source the Trends picker uses, so the two screens stop disagreeing: this one said
  // "Appliance 1" and "VF Contact" where Trends says "S1: Sanitiser" and "VF1: Spa Heater".
  const { registerData, controllerResponded } = useConnectionContext();
  const registerByAddress = useMemo(() => {
    const byAddress = new Map();
    (registerData ?? []).forEach((item) => {
      if (!byAddress.has(item.address)) byAddress.set(item.address, item);
    });
    return byAddress;
  }, [registerData]);
  const getRegisterData = (address) => registerByAddress.get(address);

  // The generic wording stays as the fallback, so an offline controller reads exactly as it
  // does today rather than going blank. ids 15/16 (Acid/Chlorine Dosing) are not in the
  // appliance slot map at all, so they always take it.
  const applianceTitle = (id, fallback) => getApplianceLabel({ id }, getRegisterData, controllerResponded) ?? fallback;

  // Appliances logs at a 5-minute interval, so a multi-day range produces
  // thousands of StatusTracker segments per appliance/pump, rendering as
  // nothing useful or hanging the tab. selectedDateRange is shared with
  // Trends/Faults (the app's own default is [startOfYesterday, endOfToday],
  // and Trends allows up to 30 days), so mutating it on every day change
  // would cause Trends/Faults to lose their own wider range each time this
  // tab was visited. The single day being viewed here is local state
  // instead: picking a day already within the current selectedDateRange
  // just slices the already-fetched data client-side (no extra fetch, and
  // selectedDateRange is untouched). Picking a day outside it, though, has
  // no client-side data to slice — that's a deliberate "go look at a
  // different day" action, so it DOES trigger a real fetch scoped to that
  // single day, same as Refresh already does; the shared selectedDateRange
  // becomes that day, so Trends/Faults will show just that day too if
  // visited next, until their own picker widens it again.
  const [viewedDay, setViewedDay] = useState(() => {
    const range = Array.isArray(selectedDateRange) ? selectedDateRange : [];
    // Normalized to the start of the day: rsuite's oneTap calendar already
    // hands handleDayChange a start-of-day Date, so this keeps viewedDay's
    // shape consistent regardless of source (isSameDay's own filtering
    // behavior below doesn't care either way, since it ignores time-of-day
    // entirely — this is purely for a consistent invariant).
    return startOfDay(range[1] ?? new Date());
  });

  const fetchedRange = Array.isArray(selectedDateRange) ? selectedDateRange : [];
  const [fetchedStart, fetchedEnd] = fetchedRange;

  // Whether a day has no client-side data to slice (outside what's
  // currently fetched) — decides whether picking it needs a real fetch.
  const isOutsideFetchedRange = (date) => {
    if (!fetchedStart || !fetchedEnd) return true;
    return isBefore(startOfDay(date), startOfDay(fetchedStart)) || isAfter(startOfDay(date), startOfDay(fetchedEnd));
  };

  // Filters an already-fetched multi-day tracker-block array (each block
  // now carries a real dateTime — see useDevices.js's getTrackerdataForAppliances_with_id/
  // getPumpTrackerdata) down to just the entries for viewedDay.
  const forViewedDay = (blocks) =>
    (Array.isArray(blocks) ? blocks : []).filter((block) => block.dateTime && isSameDay(block.dateTime, viewedDay));

  const get_app_data = (offset) => forViewedDay(AppTrackerData[offset]);
  const pumpDataForViewedDay = forViewedDay(pumpTrackerData);

  // Refresh re-fetches whatever the shared selectedDateRange currently is
  // (unchanged behaviour) — independent of which single day is being
  // locally viewed here. Wiring the Button directly to
  // onSelectedDateRangeChanged would instead pass the click's SyntheticEvent
  // as the new "range" (onSelectedDateRangeChanged is refreshDataByDate(range),
  // which calls setSelectedDateRange(range)), corrupting selectedDateRange
  // into a non-array event object — re-invoke it with the real current
  // range instead, matching HistoryView.js's refreshFaultView. If
  // selectedDateRange itself isn't a valid array, forwarding it verbatim
  // would just re-broadcast that same malformed value back into the shared
  // state via setSelectedDateRange — fall back to the currently viewed
  // day's own bounds instead, consistent with the same Array.isArray guard
  // viewedDay's init and isOutsideFetchedRange already use.
  const refreshAppliancesView = () => {
    const range = Array.isArray(selectedDateRange) ? selectedDateRange : [startOfDay(viewedDay), endOfDay(viewedDay)];
    onSelectedDateRangeChanged(range);
  };

  const handleDayChange = (day) => {
    if (!day) return;
    setViewedDay(startOfDay(day));
    if (isOutsideFetchedRange(day)) {
      onSelectedDateRangeChanged([startOfDay(day), endOfDay(day)]);
    }
  };

  return (
    <div className="mt-3">
      <Flex justifyContent="center" alignItems="center" className="space-x-2">
        <DatePicker
          value={viewedDay}
          onChange={handleDayChange}
          onOk={handleDayChange}
          cleanable={false}
          oneTap
          editable={false}
          shouldDisableDate={(date) => isAfter(startOfDay(date), startOfDay(new Date()))}
          format={datetimeFormats.picker}
          isoWeek
        />
        <Button
          color={COLOURS.default}
          size="sm"
          onClick={refreshAppliancesView}
          disabled={selectedDevice.length === 0 || loading}
        >
          Refresh
        </Button>
      </Flex>

      {loading ? (
        <LoadingView progress={progress} />
      ) : selectedDevice.length === 0 ? (
        <Card decoration="bottom" decorationColor={COLOURS.cardDecoration} className="mt-3">
          <TextTremor color={COLOURS.warning}>Select a controller</TextTremor>
        </Card>
      ) : (
        <div></div>
      )}

      {isLoadingOrNoDevice ? (
        <div></div>
      ) : (
        <>
          {pumpTrackerData.length === 0 ? (
            <div></div>
          ) : (
            <Card decoration="bottom" decorationColor={COLOURS.cardDecoration} className="mt-3  max-w-none">
              <Flex justifyContent="center" className="mt-4">
                <h3 style={{ margin: 0 }}>Appliances</h3>
              </Flex>

              <Title>Pump</Title>
              <StatusTracker className="mt-2" data={pumpDataForViewedDay}></StatusTracker>
              {AppTrackerData === undefined || AppTrackerData === null || AppTrackerData.length === 0 ? (
                <div></div>
              ) : (
                <div>
                  {isApplianceConfigured(1) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(1, 'Appliance 1')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(1)} />
                    </div>
                  )}
                  {isApplianceConfigured(2) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(2, 'Appliance 2')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(2)} />
                    </div>
                  )}
                  {isApplianceConfigured(3) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(3, 'Appliance 3')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(3)} />
                    </div>
                  )}
                  {isApplianceConfigured(4) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(4, 'Appliance 4')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(4)} />
                    </div>
                  )}
                  {isApplianceConfigured(5) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(5, 'Appliance 5')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(5)} />
                    </div>
                  )}
                  {isApplianceConfigured(6) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(6, 'Appliance 6')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(6)} />
                    </div>
                  )}
                  {isApplianceConfigured(7) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(7, 'Appliance 7')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(7)} />
                    </div>
                  )}
                  {isApplianceConfigured(8) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(8, 'Appliance 8')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(8)} />
                    </div>
                  )}
                  {isApplianceConfigured(9) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(9, 'Valve 1')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(9)} />
                    </div>
                  )}
                  {isApplianceConfigured(10) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(10, 'Valve 2')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(10)} />
                    </div>
                  )}
                  {isApplianceConfigured(11) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(11, 'Valve 3')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(11)} />
                    </div>
                  )}
                  {isApplianceConfigured(12) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(12, 'Valve 4')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(12)} />
                    </div>
                  )}
                  {isApplianceConfigured(13) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(13, 'VF Contact')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(13)} />
                    </div>
                  )}
                  {isApplianceConfigured(14) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(14, 'VF Contact 2')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(14)} />
                    </div>
                  )}
                  {isApplianceConfigured(15) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(15, 'Acid Dosing')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(15)} />
                    </div>
                  )}
                  {isApplianceConfigured(16) === false ? (
                    <div></div>
                  ) : (
                    <div>
                      <Title>{applianceTitle(16, 'Chlorine Dosing')}</Title>
                      <StatusTracker className="mt-2" data={get_app_data(16)} />
                    </div>
                  )}
                </div>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
};
